import { useCodeListQuery } from "Api/Queries/CodeList/useCodeListQuery";
import type { Region } from "Infrastructure/Api/Api";
import type { CodeListItem } from "Models/ICodeListDto";
import { useCountryNameResolver } from "Translations/countryNames";
import { Resources, useResource } from "Translations/Resources";
import { useMemo } from "react";

export const useRegionList = () => {
  const { getCountryName } = useCountryNameResolver();
  const { t } = useResource();

  const { data, isLoading, isError } = useCodeListQuery();
  const regions = data?.regions;
  // Older API without the field ⇒ everything enabled — the BE is the authority on what is open.
  const enabledRegions = useMemo<Array<Region>>(
    () => data?.enabledRegions ?? regions ?? [],
    [data?.enabledRegions, regions],
  );

  const codeListRegions = useMemo<Array<CodeListItem<Region>>>(
    () =>
      regions?.map((code) => {
        const isEnabled = enabledRegions.includes(code);
        const name = getCountryName(code);
        return {
          code,
          // The "coming soon" note lives in the label itself — always visible, works in the
          // mobile shells (no hover), and needs no change to the shared Dropdown.
          name: isEnabled
            ? name
            : `${name} (${t(Resources.Components.CountrySelector.ComingSoon)})`,
          disabled: !isEnabled,
        };
      }) ?? [],
    [regions, enabledRegions, getCountryName, t],
  );

  return {
    isLoading,
    isError,
    regions: regions ?? [],
    enabledRegions,
    codeListRegions,
  };
};
